export function createTelegramBootstrapManager({
  windowRef = window,
  documentRef = document,
  showOverlay,
  hideOverlay,
  scheduleAuthRetry,
  ensureAchievementFeedPolling,
  showScreen,
  getShowScreenRef, // Функция для получения актуальной ссылки на showScreen
  isAuthCompleted,
  getTelegramWebApp,
  setTelegramWebApp,
  getCurrentUser,
  mainAppContainer,
}) {
  if (typeof showOverlay !== "function") {
    throw new Error("showOverlay callback is required");
  }
  if (typeof hideOverlay !== "function") {
    throw new Error("hideOverlay callback is required");
  }
  if (typeof scheduleAuthRetry !== "function") {
    throw new Error("scheduleAuthRetry callback is required");
  }
  if (typeof showScreen !== "function") {
    throw new Error("showScreen callback is required");
  }

        function ensureTelegramBootstrapped() {
          // НЕ инициализируем если доступ запрещен
          if (window.__accessDenied) {
            console.log("[BOOTSTRAP] ⚠️ Access denied - skipping bootstrap");
            return;
          }
    if (isAuthCompleted?.()) {
      console.log("[BOOTSTRAP] ✅ Auth already completed, checking user access...");
      const currentUser = getCurrentUser?.();
      if (currentUser) {
        // Пользователь авторизован и имеет доступ - показываем интерфейс
        hideOverlay();
        if (mainAppContainer) {
          mainAppContainer.style.display = "block";
          mainAppContainer.style.visibility = "visible";
        }
        // Дополнительно скрываем overlay через задержки для надежности
        windowRef.setTimeout(() => {
          hideOverlay();
          if (mainAppContainer) {
            mainAppContainer.style.display = "block";
            mainAppContainer.style.visibility = "visible";
          }
        }, 100);
      } else {
        // Авторизация завершена, но пользователь не имеет доступа - скрываем интерфейс
        console.log("[BOOTSTRAP] ⚠️ Auth completed but user has no access");
        if (mainAppContainer) {
          mainAppContainer.style.display = "none";
          mainAppContainer.style.visibility = "hidden";
        }
        showOverlay(
          "Доступ ограничен. Свяжитесь с командой, чтобы получить доступ.",
          false,
          "Доступ ограничен"
        );
      }
      return;
    }
    if (
      typeof windowRef.Telegram === "undefined" ||
      !windowRef.Telegram.WebApp
    ) {
      console.log("[BOOTSTRAP] ⏳ Waiting for Telegram WebApp SDK...", {
        hasTelegram: typeof windowRef.Telegram !== "undefined",
        hasWebApp: !!(windowRef.Telegram?.WebApp),
        ts: new Date().toISOString()
      });
      showOverlay(
        "Подождите несколько секунд, мы инициализируем Telegram WebApp.",
        false,
      );
      windowRef.setTimeout(() => {
        if (windowRef.Telegram?.WebApp) {
          ensureTelegramBootstrapped();
        } else {
          // Если через 5 секунд WebApp все еще не загружен, пробуем еще раз
          windowRef.setTimeout(() => {
            ensureTelegramBootstrapped();
          }, 4000);
        }
      }, 1000);
      return;
    }

    const telegramApp =
      getTelegramWebApp?.() ?? windowRef.Telegram?.WebApp ?? null;
    if (!telegramApp) {
      console.log("[BOOTSTRAP] ❌ Telegram WebApp not available");
      return;
    }
    
    console.log("[BOOTSTRAP] ✅ Telegram WebApp found, initializing...", {
      hasInitData: !!telegramApp.initData,
      initDataLength: telegramApp.initData?.length || 0,
      hasInitDataUnsafe: !!telegramApp.initDataUnsafe,
      initDataUnsafeType: typeof telegramApp.initDataUnsafe,
      platform: telegramApp.platform,
      version: telegramApp.version,
      ts: new Date().toISOString()
    });
    
    // Сначала вызываем ready(), затем expand()
    telegramApp.ready();
    
    // Функция для расширения WebApp (поддержка обеих ориентаций)
    const doExpand = () => {
      try {
        telegramApp.expand();
        telegramApp.enableClosingConfirmation = false;
        
        // setHeaderColor и setBackgroundColor доступны только в версии 6.1+
        const version = parseFloat(telegramApp.version || '0');
        if (version >= 6.1) {
          telegramApp.setHeaderColor('#0F0F1A');
          telegramApp.setBackgroundColor('#0F0F1A');
        }
        
        // Определяем текущую ориентацию
        const isLandscape = windowRef.innerWidth > windowRef.innerHeight;
        const orientation = isLandscape ? 'landscape' : 'portrait';
        
        const isExpanded = telegramApp.isExpanded;
        console.log("[BOOTSTRAP] ✅ WebApp расширен на весь экран", {
          isExpanded,
          orientation,
          viewportWidth: telegramApp.viewportWidth || windowRef.innerWidth,
          viewportHeight: telegramApp.viewportHeight || windowRef.innerHeight,
          viewportStableHeight: telegramApp.viewportStableHeight,
          platform: telegramApp.platform
        });
        
        // Обновляем CSS класс для ориентации
        if (documentRef.documentElement) {
          documentRef.documentElement.classList.toggle('orientation-landscape', isLandscape);
          documentRef.documentElement.classList.toggle('orientation-portrait', !isLandscape);
        }
      } catch (error) {
        console.warn("[BOOTSTRAP] ⚠️ Ошибка расширения WebApp:", error);
      }
    };
    
    // Расширяем сразу
    doExpand();
    
    // Обработка события изменения viewport (включая изменение ориентации)
    if (telegramApp.onEvent) {
      telegramApp.onEvent('viewportChanged', () => {
        const newIsLandscape = windowRef.innerWidth > windowRef.innerHeight;
        const newOrientation = newIsLandscape ? 'landscape' : 'portrait';
        
        console.log("[BOOTSTRAP] 📐 Viewport changed, re-expanding...", {
          orientation: newOrientation,
          viewportWidth: telegramApp.viewportWidth || windowRef.innerWidth,
          viewportHeight: telegramApp.viewportHeight || windowRef.innerHeight,
          viewportStableHeight: telegramApp.viewportStableHeight
        });
        
        // Обновляем CSS класс для ориентации
        if (documentRef.documentElement) {
          documentRef.documentElement.classList.toggle('orientation-landscape', newIsLandscape);
          documentRef.documentElement.classList.toggle('orientation-portrait', !newIsLandscape);
        }
        
        setTimeout(doExpand, 100);
      });
    }
    
    setTelegramWebApp?.(telegramApp);

    // НЕ показываем интерфейс сразу - ждем проверки whitelist через авторизацию
    // Интерфейс будет показан только после успешной авторизации с доступом
    // Скрываем overlay временно, но интерфейс пока не показываем
    hideOverlay();
    if (mainAppContainer) {
      // Скрываем интерфейс до проверки доступа
      mainAppContainer.style.display = "none";
      mainAppContainer.style.visibility = "hidden";
    }
    
    // Расширяем на весь экран с задержками для надежности
    setTimeout(doExpand, 100);
    setTimeout(doExpand, 500);
    setTimeout(doExpand, 1000);
    ensureAchievementFeedPolling?.();
    
    // Настраиваем кнопку "Назад" с небольшой задержкой, чтобы убедиться, что все готово
    // Также настраиваем повторно после инициализации router
    setTimeout(() => {
      configureBackButton(telegramApp);
    }, 100);
    
    // Дополнительная настройка после задержки для надежности
    setTimeout(() => {
      configureBackButton(telegramApp);
    }, 1000);
    
    applyThemeFromTelegram(telegramApp);
    
    // НЕ показываем интерфейс автоматически - ждем успешной авторизации
    
    console.log("[BOOTSTRAP] 📅 Scheduling auth retry in 100ms...");
    windowRef.setTimeout(() => {
      console.log("[BOOTSTRAP] 🔄 Calling scheduleAuthRetry...");
      scheduleAuthRetry();
    }, 100);
  }

  function configureBackButton(telegramApp) {
    if (!telegramApp?.BackButton) {
      console.log('[BackButton] ⚠️ BackButton not available');
      return;
    }
    
    console.log('[BackButton] ✅ Configuring back button handler', {
      hasShowScreen: typeof showScreen === 'function',
      showScreenType: typeof showScreen,
      hasGetShowScreenRef: typeof getShowScreenRef === 'function',
      BackButtonExists: !!telegramApp.BackButton,
      BackButtonType: typeof telegramApp.BackButton
    });
    
    // Функция для получения актуальной функции showScreen
    const getCurrentShowScreen = function() {
      if (typeof getShowScreenRef === 'function') {
        const ref = getShowScreenRef();
        if (ref && typeof ref.fn === 'function') {
          return ref.fn;
        }
      }
      // Fallback на переданную функцию
      return showScreen;
    };
    
    // Сохраняем функцию в window для глобального доступа (для отладки)
    if (windowRef) {
      windowRef.__handleBackButton = function() {
        console.log('[BackButton] 🔙 Global handler called');
        const activeScreen = documentRef.querySelector(".screen.active");
        if (activeScreen && activeScreen.id !== "home-screen") {
          const currentShowScreen = getCurrentShowScreen();
          if (typeof currentShowScreen === 'function') {
            currentShowScreen("home");
          } else {
            console.error('[BackButton] ❌ currentShowScreen is not a function');
          }
        } else {
          telegramApp.BackButton.hide();
        }
      };
    }
    
    // Удаляем старый обработчик, если он есть
    try {
      if (typeof telegramApp.BackButton.offClick === 'function') {
        telegramApp.BackButton.offClick();
      }
    } catch (e) {
      console.log('[BackButton] ⚠️ Could not remove old handler:', e.message);
    }
    
    // Создаем обработчик, который всегда использует актуальную функцию showScreen
    const backButtonHandler = function() {
      console.log('[BackButton] 🔙 Back button pressed - handler called!', {
        timestamp: new Date().toISOString(),
        hasTelegramApp: !!telegramApp,
        hasBackButton: !!telegramApp?.BackButton
      });
      
      // Предотвращаем множественные вызовы
      if (backButtonHandler._processing) {
        console.log('[BackButton] ⚠️ Handler already processing, skipping');
        return;
      }
      
      backButtonHandler._processing = true;
      
      try {
        // Получаем актуальную функцию showScreen каждый раз при нажатии
        const currentShowScreen = getCurrentShowScreen();
        
        console.log('[BackButton] 🔍 Current showScreen:', {
          type: typeof currentShowScreen,
          isFunction: typeof currentShowScreen === 'function'
        });
        
        if (typeof currentShowScreen !== 'function') {
          console.error('[BackButton] ❌ showScreen is not a function:', typeof currentShowScreen);
          if (telegramApp?.BackButton) {
            telegramApp.BackButton.hide();
          }
          return;
        }
        
        const activeScreen = documentRef.querySelector(".screen.active");
        console.log('[BackButton] 📱 Active screen:', {
          id: activeScreen?.id || 'none',
          className: activeScreen?.className || 'none',
          found: !!activeScreen
        });
        
        if (activeScreen && activeScreen.id !== "home-screen") {
          console.log('[BackButton] 🏠 Navigating to home screen, calling showScreen("home")');
          try {
            // Вызываем синхронно для немедленного эффекта
            currentShowScreen("home");
            console.log('[BackButton] ✅ showScreen("home") called successfully');
            
            // Проверяем результат через небольшую задержку
            setTimeout(() => {
              const newActiveScreen = documentRef.querySelector(".screen.active");
              console.log('[BackButton] 🔍 After navigation, active screen:', {
                id: newActiveScreen?.id || 'none',
                isHome: newActiveScreen?.id === 'home-screen'
              });
              
              // Синхронизируем видимость кнопки
              syncBackButtonVisibility(telegramApp);
              backButtonHandler._processing = false;
            }, 50);
          } catch (screenError) {
            console.error('[BackButton] ❌ Error calling showScreen:', screenError);
            console.error('[BackButton] Error stack:', screenError.stack);
            backButtonHandler._processing = false;
            throw screenError;
          }
        } else {
          console.log('[BackButton] 👋 Already on home screen, hiding back button');
          if (telegramApp?.BackButton) {
            telegramApp.BackButton.hide();
          }
          backButtonHandler._processing = false;
        }
      } catch (error) {
        console.error('[BackButton] ❌ Error handling back button:', error);
        console.error('[BackButton] Error stack:', error.stack);
        // Fallback: просто скрываем кнопку
        try {
          if (telegramApp?.BackButton) {
            telegramApp.BackButton.hide();
          }
        } catch (e) {
          console.error('[BackButton] ❌ Error hiding button:', e);
        }
        backButtonHandler._processing = false;
      }
    };
    
    // Устанавливаем обработчик
    try {
      // Сначала удаляем все существующие обработчики
      try {
        if (typeof telegramApp.BackButton.offClick === 'function') {
          telegramApp.BackButton.offClick();
        }
      } catch (e) {
        console.log('[BackButton] ⚠️ Could not remove old handler before registration:', e.message);
      }
      
      // Регистрируем обработчик через onClick
      if (typeof telegramApp.BackButton.onClick === 'function') {
        telegramApp.BackButton.onClick(backButtonHandler);
        console.log('[BackButton] ✅ Handler registered successfully via onClick');
        
        // Проверяем, что обработчик действительно зарегистрирован
        if (telegramApp.BackButton.isVisible) {
          console.log('[BackButton] 📊 BackButton.isVisible:', telegramApp.BackButton.isVisible());
        }
      } else {
        console.error('[BackButton] ❌ BackButton.onClick is not a function', {
          BackButtonType: typeof telegramApp.BackButton,
          BackButtonKeys: Object.keys(telegramApp.BackButton || {}),
          onClickType: typeof telegramApp.BackButton.onClick
        });
        
        // Альтернативный способ: пробуем использовать addEventListener, если доступен
        if (telegramApp.BackButton.addEventListener) {
          console.log('[BackButton] 🔄 Trying addEventListener instead');
          telegramApp.BackButton.addEventListener('click', backButtonHandler);
        }
      }
      
      // Также пробуем использовать глобальный обработчик событий как fallback
      // Telegram WebApp может отправлять события через postMessage
      if (windowRef) {
        // Обработчик для postMessage от Telegram WebApp
        const postMessageHandler = function(event) {
          // Проверяем, что это событие от Telegram WebApp
          if (event && event.data && typeof event.data === 'string') {
            try {
              const data = JSON.parse(event.data);
              if (data && data.eventType === 'back_button_pressed') {
                console.log('[BackButton] 🔙 PostMessage event handler triggered');
                backButtonHandler();
              }
            } catch (e) {
              // Не JSON, игнорируем
            }
          }
        };
        
        // Слушаем postMessage события
        if (windowRef.addEventListener) {
          windowRef.addEventListener('message', postMessageHandler);
          console.log('[BackButton] ✅ PostMessage event listener registered');
        }
        
        // Также пробуем слушать прямое событие (если Telegram WebApp его отправляет)
        const directEventHandler = function(event) {
          if (event && (event.type === 'back_button_pressed' || event.detail === 'back_button_pressed')) {
            console.log('[BackButton] 🔙 Direct event handler triggered');
            backButtonHandler();
          }
        };
        
        if (windowRef.addEventListener) {
          windowRef.addEventListener('back_button_pressed', directEventHandler);
          console.log('[BackButton] ✅ Direct event listener registered');
        }
      }
    } catch (error) {
      console.error('[BackButton] ❌ Error registering handler:', error);
      console.error('[BackButton] Error details:', {
        message: error.message,
        stack: error.stack,
        BackButtonType: typeof telegramApp.BackButton,
        onClickType: typeof telegramApp.BackButton.onClick
      });
    }
    
    syncBackButtonVisibility(telegramApp);
  }

  function syncBackButtonVisibility(telegramApp) {
    if (!telegramApp?.BackButton) {
      return;
    }
    
    const activeScreen = documentRef.querySelector(".screen.active");
    const shouldShow = activeScreen && activeScreen.id !== "home-screen";
    
    console.log('[BackButton] 🔄 Syncing visibility:', {
      activeScreen: activeScreen?.id || 'none',
      shouldShow
    });
    
    if (shouldShow) {
      telegramApp.BackButton.show();
    } else {
      telegramApp.BackButton.hide();
    }
  }

  function applyThemeFromTelegram(telegramApp) {
    const theme = telegramApp?.themeParams;
    if (!theme) {
      return;
    }
    const rootStyle = documentRef.documentElement.style;
    rootStyle.setProperty(
      "--tg-theme-bg-color",
      theme.bg_color || "#ffffff",
    );
    rootStyle.setProperty(
      "--tg-theme-text-color",
      theme.text_color || "#000000",
    );
    rootStyle.setProperty(
      "--tg-theme-hint-color",
      theme.hint_color || "#999999",
    );
    rootStyle.setProperty(
      "--tg-theme-link-color",
      theme.link_color || "#3390ec",
    );
    rootStyle.setProperty(
      "--tg-theme-secondary-bg-color",
      theme.secondary_bg_color || "#f1f1f1",
    );
    rootStyle.setProperty(
      "--tg-theme-button-color",
      theme.button_color || "#3390ec",
    );
    rootStyle.setProperty(
      "--tg-theme-button-text-color",
      theme.button_text_color || "#ffffff",
    );
  }

  // Экспортируем функцию для переконфигурации кнопки "Назад"
  function reconfigureBackButton() {
    const telegramApp = getTelegramWebApp?.();
    if (telegramApp) {
      console.log('[BackButton] 🔄 Reconfiguring back button after router initialization');
      configureBackButton(telegramApp);
    }
  }

  return {
    ensureTelegramBootstrapped,
    reconfigureBackButton,
  };
}
